import React, { useState, useEffect, useRef } from "react";
import {
  IoMdLock,
  IoMdClose,
  IoMdPerson,
  IoMdTime,
  IoMdNotifications,
} from "react-icons/io";
import { LuPower } from "react-icons/lu";
import { MdKeyboardArrowDown, MdMenu, MdLocationOn } from "react-icons/md";
import { useGlobalContext } from "../../context/stateContext";
import Logo from "../../assets/main_logo.jpg";
import { formatRoleDisplay, logoutUser } from "../../utils/helpers";
import { useAuth } from "../../context/Auth";
import { toast } from "react-toastify";

const TopBar = () => {
  const { requestWithAuth } = useAuth();
  const { activeSession, currentUser } = useGlobalContext();
  const [showDropdown, setShowDropdown] = useState(false);
  const [showMobileMenu, setShowMobileMenu] = useState(false);
  const [showNotifications, setShowNotifications] = useState(false);
  const [notifications, setNotifications] = useState([]);
  const [unreadCount, setUnreadCount] = useState(0);
  const notificationInterval = useRef(null);

  const InfoBadge = ({
    icon: Icon,
    label,
    value,
    color = "bg-emerald-500",
  }) => (
    <div className="flex items-center gap-2 px-3 py-2 bg-white/10 backdrop-blur-sm">
      <Icon size={14} className="text-white/80" />
      <div className="flex items-center gap-2">
        <span className="text-white/80 text-xs">{label}:</span>
        <span
          className={`${color} text-white px-2 py-1 rounded text-xs font-medium capitalize`}
        >
          {value}
        </span>
      </div>
    </div>
  );

  // Fetch unread notifications
  const fetchNotifications = async () => {
    try {
      const response = await requestWithAuth(
        `/inventory/notifications/unread/`,
        "GET"
      );

      setNotifications(response.notifications || response);
      setUnreadCount(response.count || response.length);
    } catch (error) {
      // toast.error('Error fetching notifications');
    }
  };

  // Mark notification as read
  const markAsRead = async (notificationId) => {
    try {
      const response = await requestWithAuth(
        `/inventory/notifications/${notificationId}/mark-read/`,
        "POST"
      );
      toast.success(response.message);
      fetchNotifications();
    } catch (error) {
      console.error("Error marking notification as read:", error);
    }
  };

  // Mark all as read
  const markAllAsRead = async () => {
    try {
      const response = await requestWithAuth(
        `/inventory/notifications/mark-all-read/`,
        "POST"
      );
      toast.success(response.message);
      fetchNotifications();
    } catch (error) {
      console.error("Error marking all as read:", error);
    }
  };

  // useEffect(() => {
  //   fetchNotifications();

  //   notificationInterval.current = setInterval(() => {
  //     fetchNotifications();
  //   }, 60000);

  //   return () => {
  //     if (notificationInterval.current) {
  //       clearInterval(notificationInterval.current);
  //     }
  //   };
  // }, []);

  // Format timestamp
  const formatTime = (timestamp) => {
    const date = new Date(timestamp);
    const now = new Date();
    const diff = Math.floor((now - date) / 1000); // seconds

    if (diff < 60) return "Just now";
    if (diff < 3600) return `${Math.floor(diff / 60)}m ago`;
    if (diff < 86400) return `${Math.floor(diff / 3600)}h ago`;
    return date.toLocaleDateString();
  };

  let role = activeSession?.user?.role
  if (role == "branch_manager" && activeSession?.branch?.name !== currentUser?.branch) {
    role = "dispenser"
  }

  return (
    <div className="fixed top-0 left-0 right-0 bg-gradient-to-r from-slate-800 via-slate-700 to-slate-800 shadow-lg border-b border-slate-600 z-50">
      <div className="max-w-7xl mx-auto px-4">
        <div className="flex items-center justify-between">
          {/* Logo/Brand */}
          <div className="flex items-center gap-3">
            <div className="w-8 h-8 bg-gradient-to-br from-emerald-400 to-emerald-600 rounded-lg flex items-center justify-center">
              <img src={Logo} alt="" />
            </div>
            <h1 className="text-lg text-white tracking-wide">Life Chemist</h1>
          </div>

          {/* Desktop Info Badges */}
          <div className="hidden lg:flex items-center">
            <InfoBadge
              icon={MdLocationOn}
              label="Branch"
              value={activeSession?.branch?.name}
              color="bg-blue-500"
            />

            <InfoBadge
              icon={IoMdTime}
              label="Session"
              value={activeSession?.session}
              color="bg-orange-500"
            />
          </div>

          {/* Notification Bell & User Menu */}
          <div className="flex items-center gap-2">
            {/* Notification Bell */}
            <div className="hidden lg:block relative">
              <button
                onClick={() => setShowNotifications(!showNotifications)}
                className="relative p-2 text-white hover:bg-white/10 rounded-lg transition-colors"
              >
                <IoMdNotifications size={22} />
                {unreadCount > 0 && (
                  <span className="absolute -top-1 -right-1 bg-red-500 text-white text-xs rounded-full w-5 h-5 flex items-center justify-center font-medium">
                    {unreadCount > 9 ? "9+" : unreadCount}
                  </span>
                )}
              </button>

              {/* Notifications Dropdown */}
              {showNotifications && (
                <div className="absolute right-0 top-full mt-2 w-96 bg-white rounded-xl shadow-2xl border border-gray-100 z-50 max-h-96 overflow-hidden flex flex-col">
                  <div className="px-4 py-3 border-b border-gray-100 flex items-center justify-between bg-gray-50">
                    <h3 className="text-sm font-semibold text-gray-900">
                      Price Updates
                    </h3>
                    {unreadCount > 0 && (
                      <button
                        onClick={markAllAsRead}
                        className="text-xs text-blue-600 hover:text-blue-700 font-medium"
                      >
                        Mark all read
                      </button>
                    )}
                  </div>

                  <div className="overflow-y-auto flex-1">
                    {notifications.length === 0 ? (
                      <div className="px-4 py-8 text-center text-gray-500 text-sm">
                        No new price updates
                      </div>
                    ) : (
                      <div className="divide-y divide-gray-100">
                        {notifications.map((notif) => (
                          <div
                            key={notif.id}
                            onClick={() => markAsRead(notif.id)}
                            className="px-4 py-3 hover:bg-gray-50 cursor-pointer transition-colors"
                          >
                            <div className="flex items-start justify-between gap-2 mb-1">
                              <div className="font-medium text-sm text-gray-900 line-clamp-1">
                                {notif.product_name || notif.medicine_name}
                              </div>
                              <span className="text-xs text-gray-500 whitespace-nowrap">
                                {formatTime(
                                  notif.created_at || notif.updated_at
                                )}
                              </span>
                            </div>
                            <div className="flex items-center gap-2 text-sm">
                              {notif.message}
                            </div>
                          </div>
                        ))}
                      </div>
                    )}
                  </div>
                </div>
              )}
            </div>

            {/* Desktop User Menu */}
            <div className="hidden lg:block relative">
              <button
                onClick={() => setShowDropdown(!showDropdown)}
                className="flex items-center gap-3 px-4 py-2 bg-white/10 hover:bg-white/20 transition-all duration-200 backdrop-blur-sm"
              >
                <div className="w-8 h-8 bg-gradient-to-br from-emerald-400 to-emerald-600 rounded-full flex items-center justify-center">
                  <IoMdPerson className="text-white" />
                </div>
                <div className="text-left">
                  <div className="text-white font-medium text-sm">
                    {activeSession?.user?.first_name}{" "}
                    {activeSession?.user?.last_name}
                  </div>
                  <div className="text-white/70 text-xs">
                    {formatRoleDisplay(role)}
                  </div>
                </div>
                <MdKeyboardArrowDown
                  className={`text-white/80 transition-transform duration-200 ${showDropdown ? "rotate-180" : ""
                    }`}
                />
              </button>

              {/* Dropdown Menu */}
              {showDropdown && (
                <div className="absolute right-0 top-full mt-2 w-56 bg-white rounded-xl shadow-2xl border border-gray-100 py-2 z-50">
                  <div className="px-4 py-3 border-b border-gray-100">
                    <div className="text-sm font-medium text-gray-900">
                      {activeSession?.user?.first_name}{" "}
                      {activeSession?.user?.last_name}
                    </div>
                    <div className="text-xs text-gray-500">
                      {activeSession?.user?.role}
                    </div>
                  </div>

                  {/* <button
                    onClick={() => console.log("Navigate to change password")}
                    className="w-full px-4 py-3 text-left text-sm text-gray-700 hover:bg-gray-50 flex items-center gap-3 transition-colors"
                  >
                    <IoMdLock className="text-gray-500" />
                    Change Password
                  </button> */}

                  <hr className="my-1" />

                  <button
                    onClick={(e) => {
                      e.stopPropagation();
                      logoutUser();
                    }}
                    className="w-full px-4 py-3 text-left text-sm text-red-600 hover:bg-red-50 flex items-center gap-3 transition-colors"
                  >
                    <LuPower />
                    Logout
                  </button>
                </div>
              )}
            </div>

            {/* Mobile Menu Toggle */}
            <button
              onClick={() => setShowMobileMenu(!showMobileMenu)}
              className="lg:hidden p-2 text-white hover:bg-white/10 rounded-lg transition-colors"
            >
              {showMobileMenu ? <IoMdClose size={20} /> : <MdMenu size={20} />}
            </button>
          </div>
        </div>

        {/* Mobile Menu */}
        {showMobileMenu && (
          <div className="lg:hidden border-t border-slate-600 py-4 space-y-4">


            {/* Mobile Info Badges */}
            <div className="space-y-3">
              <InfoBadge
                icon={MdLocationOn}
                label="Branch"
                value={activeSession?.branch?.name}
                color="bg-blue-500"
              />
              <InfoBadge
                icon={IoMdTime}
                label="Session"
                value={activeSession?.session}
                color="bg-orange-500"
              />
            </div>

            {/* Mobile User Section */}
            <div className="border-t border-slate-600 pt-4">
              <div className="flex items-center gap-3 mb-4 px-3 py-2 bg-white/10 rounded-lg">
                <div className="w-10 h-10 bg-gradient-to-br from-emerald-400 to-emerald-600 rounded-full flex items-center justify-center">
                  <IoMdPerson className="text-white text-lg" />
                </div>
                <div>
                  <div className="text-white font-medium">
                    {activeSession?.user?.first_name}{" "}
                    {activeSession?.user?.last_name}
                  </div>
                  <div className="text-white/70 text-sm">
                    {activeSession?.user?.role}
                  </div>
                </div>
              </div>

              <div className="space-y-2">
                {/* <button
                  onClick={() => console.log("Navigate to change password")}
                  className="w-full px-3 py-2 text-left text-white hover:bg-white/10 rounded-lg flex items-center gap-3 transition-colors"
                >
                  <IoMdLock />
                  Change Password
                </button> */}

                <button
                  onClick={(e) => {
                    e.stopPropagation();
                    logoutUser();
                  }}
                  className="w-full px-3 py-2 text-left text-red-400 hover:bg-red-500/20 rounded-lg flex items-center gap-3 transition-colors"
                >
                  <LuPower />
                  Logout
                </button>
              </div>
            </div>
          </div>
        )}
      </div>

      {/* Backdrop for mobile menu */}
      {/* {showMobileMenu && (
        <div
          className="fixed inset-0 bg-black/20 z-40 lg:hidden"
          onClick={() => setShowMobileMenu(false)}
        />
      )} */}

      {/* Backdrop for dropdown */}
      {showDropdown && (
        <div
          className="fixed inset-0 z-40"
          onClick={() => setShowDropdown(false)}
        />
      )}

      {/* Backdrop for notifications */}
      {/* {showNotifications && (
        <div
          className="fixed inset-0 z-40"
          onClick={() => setShowNotifications(false)}
        />
      )} */}
    </div>
  );
};

export default TopBar;
